import React, { useEffect, useRef, useState } from 'react';

interface IframeEditorProps {
  url: string;
  onSave: (content: string) => void;
  onClose: () => void;
}

const IframeEditor: React.FC<IframeEditorProps> = ({
  url,
  onSave,
  onClose
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [iframeSrc, setIframeSrc] = useState<string>('');

  useEffect(() => {
    fetchAndCreateIframe();
  }, [url]);

  const fetchAndCreateIframe = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Try multiple CORS proxies to get the HTML content
      const proxies = [
        `https://api.allorigins.win/get?url=${encodeURIComponent(url)}`,
        `https://cors-anywhere.herokuapp.com/${url}`,
        `https://thingproxy.freeboard.io/fetch/${url}`,
        `https://api.codetabs.com/v1/proxy?quest=${url}`
      ];

      let content = null;
      let workingProxy = '';

      for (const proxy of proxies) {
        try {
          console.log(`Trying proxy: ${proxy}`);
          const response = await fetch(proxy, {
            method: 'GET',
            headers: {
              'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,image/webp,*/*;q=0.8',
              'Accept-Language': 'en-US,en;q=0.5',
              'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/91.0.4472.124 Safari/537.36'
            }
          });

          if (response.ok) {
            const data = await response.json();
            content = data.contents || data;
            workingProxy = proxy;
            console.log(`Success with proxy: ${proxy}`);
            break;
          }
        } catch (err) {
          console.log(`Proxy failed: ${proxy}`, err);
          continue;
        }
      }

      if (!content) {
        throw new Error('All proxies failed. Cannot fetch website content.');
      }

      // Clean and process the content
      let cleanedContent = content
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '') // Remove scripts
        .replace(/on\w+="[^"]*"/gi, '') // Remove event handlers
        .replace(/on\w+='[^']*'/gi, '') // Remove event handlers
        .replace(/javascript:/gi, '') // Remove javascript: protocols
        .replace(/data-src/gi, 'src') // Convert data-src to src for images
        .replace(/srcset="[^"]*"/gi, '') // Remove srcset attributes
        .replace(/sizes="[^"]*"/gi, '') // Remove sizes attributes
        .replace(/<!--[\s\S]*?-->/g, ''); // Remove HTML comments

      // Create a data URL with the cleaned content
      const dataUrl = `data:text/html;charset=utf-8,${encodeURIComponent(cleanedContent)}`;
      setIframeSrc(dataUrl);
      setIsLoading(false);

    } catch (err) {
      console.error('Error fetching website content:', err);
      setError(`Failed to fetch website content: ${err instanceof Error ? err.message : 'Unknown error'}`);
      setIsLoading(false);
    }
  };

  const enableEditing = () => {
    if (!iframeRef.current) return;

    try {
      const iframe = iframeRef.current;
      const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
      
      if (!iframeDoc) {
        setError('Cannot access iframe content. Content loaded but editing may be limited.');
        return;
      }

      // Make the content editable
      iframeDoc.body.contentEditable = 'true';
      iframeDoc.body.style.cursor = 'text';
      
      // Add some visual indicators for editing
      iframeDoc.body.style.outline = '2px solid #007bff';
      iframeDoc.body.style.outlineOffset = '10px';
      
      // Add editing styles
      const style = iframeDoc.createElement('style');
      style.textContent = `
        * {
          cursor: text !important;
        }
        *:hover {
          outline: 1px dashed #007bff !important;
        }
        [contenteditable="true"] {
          outline: 2px solid #007bff !important;
          outline-offset: 2px !important;
        }
      `;
      iframeDoc.head.appendChild(style);
      
      setIsEditing(true);
      console.log('Editing enabled in iframe');
    } catch (err) {
      console.error('Error enabling editing:', err);
      setError('Cannot enable editing due to iframe restrictions. Content is loaded but editing may be limited.');
    }
  };

  const disableEditing = () => {
    if (!iframeRef.current) return;

    try {
      const iframe = iframeRef.current;
      const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
      
      if (iframeDoc) {
        iframeDoc.body.contentEditable = 'false';
        iframeDoc.body.style.cursor = 'default';
        iframeDoc.body.style.outline = 'none';
      }
      
      setIsEditing(false);
    } catch (err) {
      console.error('Error disabling editing:', err);
    }
  };

  const saveContent = () => {
    if (!iframeRef.current) return;

    try {
      const iframe = iframeRef.current;
      const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
      
      if (iframeDoc) {
        const html = iframeDoc.documentElement.outerHTML;
        onSave(html);
      } else {
        // Fallback: get the iframe src content
        onSave(iframe.src);
      }
    } catch (err) {
      setError('Cannot save content due to iframe restrictions');
    }
  };

  const addElement = (elementType: string) => {
    if (!iframeRef.current || !isEditing) return;

    try {
      const iframe = iframeRef.current;
      const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
      
      if (!iframeDoc) return;

      let newElement: HTMLElement;
      
      switch (elementType) {
        case 'heading':
          newElement = iframeDoc.createElement('h2');
          newElement.textContent = 'New Heading';
          newElement.style.color = '#333';
          newElement.style.margin = '20px 0';
          break;
        case 'paragraph':
          newElement = iframeDoc.createElement('p');
          newElement.textContent = 'New paragraph text here. Click to edit.';
          newElement.style.lineHeight = '1.6';
          newElement.style.color = '#555';
          newElement.style.margin = '15px 0';
          break;
        case 'button':
          newElement = iframeDoc.createElement('button');
          newElement.textContent = 'Click Me';
          newElement.style.background = '#007bff';
          newElement.style.color = 'white';
          newElement.style.padding = '10px 20px';
          newElement.style.border = 'none';
          newElement.style.borderRadius = '5px';
          newElement.style.cursor = 'pointer';
          newElement.style.margin = '10px 0';
          break;
        case 'image':
          const imgElement = iframeDoc.createElement('img');
          imgElement.src = 'https://via.placeholder.com/300x200';
          imgElement.alt = 'Placeholder Image';
          imgElement.style.maxWidth = '100%';
          imgElement.style.height = 'auto';
          imgElement.style.margin = '15px 0';
          newElement = imgElement;
          break;
        case 'divider':
          newElement = iframeDoc.createElement('hr');
          newElement.style.border = '1px solid #ddd';
          newElement.style.margin = '20px 0';
          break;
        default:
          newElement = iframeDoc.createElement('div');
          newElement.textContent = 'New element';
          newElement.style.padding = '10px';
          newElement.style.border = '1px solid #ddd';
          newElement.style.margin = '10px 0';
      }

      iframeDoc.body.appendChild(newElement);
    } catch (err) {
      setError('Cannot add elements due to iframe restrictions');
    }
  };

  if (isLoading) {
    return (
      <div style={{ 
        height: '100%', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundColor: '#f8f9fa'
      }}>
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <h5>Fetching Website Content...</h5>
          <p className="text-muted">{url}</p>
          <small className="text-muted">Copying HTML content via proxy...</small>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        height: '100%', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundColor: '#f8f9fa'
      }}>
        <div className="text-center">
          <div className="text-danger mb-3">
            <i className="fas fa-exclamation-triangle fa-3x"></i>
          </div>
          <h5>Error Fetching Website</h5>
          <p className="text-muted">{error}</p>
          <button className="btn btn-primary me-2" onClick={fetchAndCreateIframe}>
            <i className="fas fa-redo me-1"></i>
            Try Again
          </button>
          <button className="btn btn-outline-secondary" onClick={onClose}>
            <i className="fas fa-times me-1"></i>
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Toolbar */}
      <div className="p-3 border-bottom bg-light">
        <div className="d-flex justify-content-between align-items-center">
          <h6 className="mb-0">
            <i className="fas fa-globe me-2"></i>
            Iframe Editor - {url}
          </h6>
          <div>
            {!isEditing ? (
              <button className="btn btn-sm btn-primary me-2" onClick={enableEditing}>
                <i className="fas fa-edit me-1"></i>
                Enable Editing
              </button>
            ) : (
              <button className="btn btn-sm btn-secondary me-2" onClick={disableEditing}>
                <i className="fas fa-eye me-1"></i>
                Disable Editing
              </button>
            )}
            <button className="btn btn-sm btn-success me-2" onClick={saveContent}>
              <i className="fas fa-save me-1"></i>
              Save
            </button>
            <button className="btn btn-sm btn-outline-danger" onClick={onClose}>
              <i className="fas fa-times me-1"></i>
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Component Library (only when editing) */}
      {isEditing && (
        <div className="p-2 border-bottom bg-light">
          <div className="d-flex gap-2">
            <button 
              className="btn btn-sm btn-outline-secondary"
              onClick={() => addElement('heading')}
            >
              <i className="fas fa-heading me-1"></i>
              Heading
            </button>
            <button 
              className="btn btn-sm btn-outline-secondary"
              onClick={() => addElement('paragraph')}
            >
              <i className="fas fa-paragraph me-1"></i>
              Paragraph
            </button>
            <button 
              className="btn btn-sm btn-outline-secondary"
              onClick={() => addElement('button')}
            >
              <i className="fas fa-square me-1"></i>
              Button
            </button>
            <button 
              className="btn btn-sm btn-outline-secondary"
              onClick={() => addElement('image')}
            >
              <i className="fas fa-image me-1"></i>
              Image
            </button>
            <button 
              className="btn btn-sm btn-outline-secondary"
              onClick={() => addElement('divider')}
            >
              <i className="fas fa-minus me-1"></i>
              Divider
            </button>
          </div>
        </div>
      )}

      {/* Iframe */}
      <div style={{ flex: 1, position: 'relative' }}>
        <iframe
          ref={iframeRef}
          src={iframeSrc}
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            backgroundColor: 'white'
          }}
          title="Website Editor"
          sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
        />
      </div>

      {/* Editing Instructions */}
      {isEditing && (
        <div className="p-3 border-top bg-light">
          <div className="alert alert-info mb-0" role="alert">
            <i className="fas fa-info-circle me-2"></i>
            <strong>Iframe Editing Active:</strong> Click on any text or element to edit it directly. 
            Original CSS and styling are preserved. Use the toolbar above to add new elements.
          </div>
        </div>
      )}

      {/* Info Panel */}
      <div className="p-2 border-top bg-light">
        <div className="alert alert-success mb-0 py-2" role="alert">
          <small>
            <i className="fas fa-check-circle me-1"></i>
            <strong>Enhanced Iframe:</strong> HTML content copied from website via proxy and displayed in iframe. 
            Original CSS preserved with CORS bypass.
          </small>
        </div>
      </div>
    </div>
  );
};

export default IframeEditor;
